'use client';

import React from 'react';
import {BgDpcCodeType} from "@/data/dtos/Resouces/BgDentalProcedureCodes/BgDpcCodeType";
import {BgDpcRecipeType} from "@/data/dtos/Resouces/BgDentalProcedureCodes/BgDpcRecipeType";
import CopyToClipboard from "@/app-components/resouces/CopyToClipboard";
import URLUtils from "@/utils/URLUtils";
import DateUtils from "@/utils/DateUtils";
import {useTranslations, useLocale} from "next-intl";
import ReviewerAvatarClient from "@/app-components/resouces/bg-dpc/ReviewerAvatarClient";
import {Link} from "@/i18n/routing";
import ResourceMarkdown from '@/app-components/resouces/ResourceMarkdown';
import IconDentalLab from '@/app-components/resouces/bg-dpc/IconDentalLab';
import IconDentalChair from '@/app-components/resouces/bg-dpc/IconDentalChair';
import IconBgNhif from '@/app-components/resouces/bg-dpc/IconBgNhif';


export type ItemDentalCodeInteractiveProps = {
	searchQuery:string;
	code:BgDpcCodeType;
	treatments:BgDpcRecipeType[];
};

export default function ItemDentalCodeInteractive({ code, treatments, searchQuery }: ItemDentalCodeInteractiveProps) {
	const locale = useLocale();

	const t = useTranslations('page.r-bg-dental-procedure-codes');
	const tCat = useTranslations( 'page.r-bg-dental-procedure-codes-catalog');
	const tSnglCode = useTranslations( 'page.r-bg-dental-procedure-codes-single-code');

	const dCode = code;
	// ToDo: - Check treatments - is wrong.
	const relatedTreatments: BgDpcRecipeType[] | null = (() => {
		const filteredRecipes = treatments.filter((lRecipe) => {
			// Check if any code in codesToUse matches dCode.code
			const mainCodeMatches = lRecipe.codesToUse.codes.some((code) => code.code.code === dCode.code);

			// Check if any code in additionalCodes matches dCode.code
			const additionalCodeMatches = lRecipe.additionalCodes?.codes.some((code) => code.code.code === dCode.code);

			// Return true if either condition matches
			return mainCodeMatches || additionalCodeMatches;
		});

		// Return null if the filteredRecipes length is 0, otherwise return the array
		return filteredRecipes.length > 0 ? filteredRecipes : null;
	})();



	return (
		<div className="card border p-4 h-100">
			<div className="card-body p-0">
				<div className="d-flex justify-content-between mb-3">
					<div>
						<Link href={code.siteLink} className="btn border btn-sm btn-light font-monospace fw-semibold me-2">
							{code.code}
						</Link>

						<CopyToClipboard textToCopy={code.code}>
							<a className="btn btn-sm btn-light btn-icon me-2" data-bs-toggle="tooltip" data-placement="top" data-bs-title={tCat('common-tooltip.copy_code')}>
								<i className="bi bi-copy" title={tCat('common-tooltip.copy_code')}></i>
							</a>
						</CopyToClipboard>
					</div>

					<CopyToClipboard textToCopy={URLUtils.path([locale, code.siteLink])}>
						<a className="btn btn-sm btn-light btn-icon" data-bs-toggle="tooltip" data-placement="top" data-bs-title={tCat('common-tooltip.copy_link')}>
							<i className="bi bi-link" title={tCat('common-tooltip.copy_link')}></i>
						</a>
					</CopyToClipboard>
				</div>

				<h3 className="card-title mb-0 h5">
					<Link href={code.siteLink} className="heading-color">
						{code.name}
					</Link>
				</h3>
				<div className="card-title mb-0 text-body heading-color">
					{code.category.title}
				</div>
				<div className="mb-3 small">
					{t('common_used.last_change') + ': ' + DateUtils.formatDate(code.updatedAt, locale)}
				</div>

					<div className="mt-3 small">
						{code.paidByNhifBg &&
							<IconBgNhif />
						}
						{code.chairSide &&
							<IconDentalChair />
						}
						{code.dentalLab &&
							<IconDentalLab />
						}
					</div>

				{code.description &&
                    <>
                        <h6 className="mt-4">{tSnglCode('overview')}</h6>
						<ResourceMarkdown>
							{code.description}
						</ResourceMarkdown>
                    </>
				}

				{code.howToUse &&
                    <>
                        <h6 className="mt-4">{tSnglCode('when_to_use')}</h6>
						<ResourceMarkdown>
							{code.howToUse}
						</ResourceMarkdown>
                    </>
				}

				{code.notes &&
                    <>
                        <h6 className="mt-4">{tSnglCode('caution')}</h6>
						<ResourceMarkdown>
							{code.notes}
						</ResourceMarkdown>
                    </>
				}


				{relatedTreatments &&
                    <>
                        <h6 className="mt-4">{tSnglCode('related_treatments_section.title')}</h6>
                        <ul className="mb-4">
							{relatedTreatments.map((recipeItem) => (
								<li key={recipeItem.slug} className="heading-color">
									<Link href={recipeItem.siteLink} className="heading-color">
										{recipeItem.name}
									</Link>
								</li>
							))}
                        </ul>
                    </>
				}

				<div className="d-sm-flex justify-content-start justify-content-sm-between text-start mt-5">
					{code.writer &&
                        <ReviewerAvatarClient role='write' author={code.writer}/>
					}

					{code.reviewer &&
                        <ReviewerAvatarClient role='review' author={code.reviewer}/>
					}
				</div>
			</div>
		</div>
	);
}
